import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { messageOf, unwrap } from '@truecare/api-client';
import { IMMUNIZATION_CATALOG } from '@truecare/schemas';
import { Alert, Card, CardHeader } from '@truecare/ui';
import { api } from '../api';

/**
 * Voluntary immunization disclosure (2026-08 vaccine-requirements revamp).
 *
 * The Ontario COVID-19 healthcare-worker mandate was suspended June 2022, so
 * vaccination is no longer a required document upload. Instead the caregiver may
 * voluntarily tick which immunizations they carry. Unlike skills this is NOT
 * role-scoped — the full IMMUNIZATION_CATALOG renders for everyone. Toggling
 * saves immediately (full-set PUT /me/caregiver/immunizations); on error the
 * checkbox reverts. Nothing here is required or expiry-tracked.
 *
 * Editable only when the profile is editable (APPLYING / ONBOARDING / ACTIVE);
 * otherwise the boxes render disabled so the caregiver still sees their picks.
 */
export function CaregiverImmunizationSection({
  currentImmunizations,
  editable,
}: {
  currentImmunizations: string[];
  editable: boolean;
}) {
  const qc = useQueryClient();
  const catalogCodes = new Set(IMMUNIZATION_CATALOG.map((i) => i.code));
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(currentImmunizations.filter((c) => catalogCodes.has(c))),
  );
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (codes: string[]) =>
      unwrap(
        api.PUT('/api/v1/me/caregiver/immunizations', {
          body: { immunizationCodes: codes },
        }),
      ),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['caregiver', 'me'] });
    },
    onError: (e) => {
      setError(messageOf(e, 'Could not save your immunizations.'));
    },
  });

  function toggle(code: string): void {
    const prev = new Set(selected);
    const next = new Set(selected);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSelected(next);
    mutation.mutate([...next], { onError: () => setSelected(prev) });
  }

  return (
    <Card padding="lg">
      <CardHeader
        title="Immunization (optional)"
        description="Voluntary — share any immunizations you've had. Nothing here is required or tracked for expiry, and it never affects whether you can be activated."
      />
      {error && (
        <Alert tone="danger" title="Couldn't save">
          {error}
        </Alert>
      )}
      <div className="mt-3 flex flex-col gap-3">
        {IMMUNIZATION_CATALOG.map((i) => (
          <label
            key={i.code}
            className="flex items-start gap-2 font-body text-sm text-forest"
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={selected.has(i.code)}
              disabled={!editable || mutation.isPending}
              onChange={() => toggle(i.code)}
              data-testid={`caregiver-immunization-${i.code}`}
            />
            <span>
              <span className="font-medium">{i.label}</span>
              <span className="block text-xs text-ink-muted">{i.description}</span>
            </span>
          </label>
        ))}
      </div>
    </Card>
  );
}
